import MarkdownIt from 'markdown-it';

type AdfMark = {
  type: 'strong' | 'em' | 'strike' | 'underline' | 'link' | 'code';
  attrs?: { href?: string };
};

type AdfNode = {
  type: string;
  content?: AdfNode[];
  attrs?: Record<string, unknown>;
  text?: string;
  marks?: AdfMark[];
};

export type AdfDocument = {
  type: 'doc';
  version: 1;
  content: AdfNode[];
};

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: false
});

const createPlainTextAdf = (text: string): AdfDocument => {
  if (!text) {
    return { type: 'doc', version: 1, content: [] };
  }

  return {
    type: 'doc',
    version: 1,
    content: [
      {
        type: 'paragraph',
        content: [{ type: 'text', text }]
      }
    ]
  };
};

const ensureContent = (node: AdfNode): AdfNode[] => {
  if (!node.content) {
    node.content = [];
  }
  return node.content;
};

const popMark = (marks: AdfMark[], type: AdfMark['type']) => {
  for (let i = marks.length - 1; i >= 0; i -= 1) {
    if (marks[i].type === type) {
      marks.splice(i, 1);
      return;
    }
  }
};

const parseInlineTokens = (tokens: MarkdownIt.Token[] = []): AdfNode[] => {
  const result: AdfNode[] = [];
  const marks: AdfMark[] = [];

  const pushText = (text: string, extraMarks: AdfMark[] = []) => {
    if (!text) return;
    const mergedMarks = [...marks, ...extraMarks];
    const node: AdfNode = {
      type: 'text',
      text
    };
    if (mergedMarks.length) {
      node.marks = mergedMarks.map((mark) => ({ ...mark }));
    }
    result.push(node);
  };

  tokens.forEach((token) => {
    switch (token.type) {
      case 'text':
        pushText(token.content);
        break;
      case 'strong_open':
        marks.push({ type: 'strong' });
        break;
      case 'strong_close':
        popMark(marks, 'strong');
        break;
      case 'em_open':
        marks.push({ type: 'em' });
        break;
      case 'em_close':
        popMark(marks, 'em');
        break;
      case 's_open':
        marks.push({ type: 'strike' });
        break;
      case 's_close':
        popMark(marks, 'strike');
        break;
      case 'link_open': {
        const href = token.attrGet('href') ?? '';
        marks.push({ type: 'link', attrs: { href } });
        break;
      }
      case 'link_close':
        popMark(marks, 'link');
        break;
      case 'code_inline':
        pushText(token.content, [{ type: 'code' }]);
        break;
      case 'hardbreak':
      case 'softbreak':
        result.push({ type: 'hardBreak' });
        break;
      case 'html_inline': {
        const html = token.content?.toLowerCase() ?? '';
        if (html.includes('<u>')) {
          marks.push({ type: 'underline' });
        } else if (html.includes('</u>')) {
          popMark(marks, 'underline');
        } else if (html.includes('<br')) {
          result.push({ type: 'hardBreak' });
        }
        break;
      }
      default:
        break;
    }
  });

  return result;
};

export const markdownToAdf = (markdown: string): AdfDocument => {
  try {
    const tokens = md.parse(markdown || '', {});
    const root: AdfNode = { type: 'doc', content: [] };
    const stack: AdfNode[] = [root];

    const pushNode = (node: AdfNode) => {
      ensureContent(stack[stack.length - 1]).push(node);
    };

    const openNode = (node: AdfNode) => {
      stack.push(node);
    };

    const closeNode = () => {
      const node = stack.pop();
      if (!node) return;

      if (node.type === 'listItem' && (!node.content || node.content.length === 0)) {
        node.content = [{ type: 'paragraph', content: [] }];
      }

      if (stack.length) {
        pushNode(node);
      }
    };

    tokens.forEach((token) => {
      switch (token.type) {
        case 'paragraph_open':
          openNode({ type: 'paragraph', content: [] });
          break;
        case 'paragraph_close':
          closeNode();
          break;
        case 'heading_open': {
          const level = Number(token.tag.replace('h', '')) || 1;
          openNode({ type: 'heading', attrs: { level }, content: [] });
          break;
        }
        case 'heading_close':
          closeNode();
          break;
        case 'bullet_list_open':
          openNode({ type: 'bulletList', content: [] });
          break;
        case 'bullet_list_close':
          closeNode();
          break;
        case 'ordered_list_open': {
          const start = token.attrGet('start');
          const attrs = start && Number(start) !== 1 ? { order: Number(start) } : undefined;
          openNode({ type: 'orderedList', attrs, content: [] });
          break;
        }
        case 'ordered_list_close':
          closeNode();
          break;
        case 'list_item_open':
          openNode({ type: 'listItem', content: [] });
          break;
        case 'list_item_close':
          closeNode();
          break;
        case 'blockquote_open':
          openNode({ type: 'blockquote', content: [] });
          break;
        case 'blockquote_close':
          closeNode();
          break;
        case 'inline': {
          const inlineNodes = parseInlineTokens(token.children || undefined);
          ensureContent(stack[stack.length - 1]).push(...inlineNodes);
          break;
        }
        case 'fence':
        case 'code_block': {
          const language = token.info?.trim();
          const content = token.content ? token.content.replace(/\n$/, '') : '';
          pushNode({
            type: 'codeBlock',
            attrs: language ? { language } : undefined,
            content: content ? [{ type: 'text', text: content }] : []
          });
          break;
        }
        case 'hr':
          pushNode({ type: 'rule' });
          break;
        default:
          break;
      }
    });

    return {
      type: 'doc',
      version: 1,
      content: root.content || []
    };
  } catch (error) {
    return createPlainTextAdf(markdown || '');
  }
};

export { createPlainTextAdf };
